import React, { useState, useEffect } from "react";
import dynamic from "next/dynamic";
import useTranslation from "next-translate/useTranslation";
import { NextPage } from "next";
import { Route } from "../../pages/api/courses";
import { NextRouter } from "next/router";
import Link from "next/link";
import { useDispatch } from "react-redux";
import {
  updateAdults,
  updateChildren,
  updateReturnDate,
} from "../../redux/searchBoxSlice";

const CustomDatePicker = dynamic(() => import("../datepicker/Datepicker"));
const AutocompletePoints = dynamic(() => import("./AutocompletePoints"));
const AutoCompleteFixed = dynamic(
  () => import("../searchbox/AutoCompleteFixed")
);

interface SearchboxProps {
  courses: Route[];
  setVehicleSelectToggle: (isVehicleSectionToggled: boolean) => void;
  scrollToEl: (elementId: string, timeout: number) => void;
  setCourse: (course: Route) => void;
  router: NextRouter;
  setMode: (mode: string) => void;
}

const Searchbox: NextPage<SearchboxProps> = ({
  courses,
  setVehicleSelectToggle,
  scrollToEl,
  setCourse,
  router,
  setMode,
}) => {
  const [start, setStart] = useState<string>("");
  const [end, setEnd] = useState<string>("");

  const { t } = useTranslation("book");

  const dispatch = useDispatch();

  const [adultsInput, setAdultsInput] = useState<string>("");
  const [childrenInput, setChildrenInput] = useState<string>("");
  const [arrivalDate, setArrivalDate] = useState<moment.Moment | null>(null);
  const [returnDate, setReturnDate] = useState<moment.Moment | null>(null);
  const [twoWayToggle, setTwoWayToggle] = useState<boolean>(false);
  const [loadingToggle, setLoadingToggle] = useState<boolean>(false);
  const [validationError, setValidationError] = useState<boolean>(false);
  const [manualMode, setManualMode] = useState<boolean>(true);
  const [notFound, setNotFound] = useState<boolean>(false);

  // Collapse the section when data is changed, also control queries
  useEffect((): void => {
    setVehicleSelectToggle(false);
    setNotFound(false);
  }, [
    arrivalDate,
    returnDate,
    adultsInput,
    childrenInput,
    start,
    end,
    twoWayToggle,
    manualMode,
    setVehicleSelectToggle
  ]);

  const isFormValid = (): boolean => {
    if (
      !start ||
      !end ||
      !adultsInput ||
      !arrivalDate ||
      (twoWayToggle && !returnDate)
    ) {
      setVehicleSelectToggle(false);
      scrollToEl("form", 50);
      return false;
    }
    return true;
  };

  const searchForCourse = (): void => {
    if (!isFormValid()) {
      setValidationError(true);
      return;
    }
    let course: Route | null;

    setValidationError(false);
    setLoadingToggle(true);
    setTimeout(() => {
      setLoadingToggle(false);

      if (manualMode) {
        course = searchManualMode();
      } else {
        // Check all courses stored in the database
        course = searchAutoMode();
        if (!course) {
          setVehicleSelectToggle(false);
          setNotFound(true);
          scrollToEl("form", 50);
          return;
        }
      }

      setCourse(course);
      setNotFound(false);
      setVehicleSelectToggle(true);
      scrollToEl("vehicles", 700);
    }, 1000);
  };

  const searchAutoMode = (): Route => {
    let course: Route | null;
    // Attempt to find course with the startInput
    course = courses.find(
      (c) =>
        c.start.toLowerCase() === start.toString().toLowerCase() &&
        c.end.toLowerCase() === end.toString().toLowerCase()
    );

    if (!course) {
      // Re-attempt to find a course with reversed logic
      course = courses.find(
        (c) =>
          c.end.toLowerCase() === start.toString().toLowerCase() &&
          c.start.toLowerCase() === end.toString().toLowerCase()
      );

      course = reverseCourseStartEnd(course);
    }

    if (course) {
      course.isTwoWay = twoWayToggle;
    }

    return course;
  };

  const searchManualMode = (): Route => {
    const course: Route = {
      id: 9999, // Id 9999 is for all manual courses if needed
      start: start,
      end: end,
      routeName: `${start} - ${end}`,
      carPrice: 0.5, // per kilometer
      minivanPrice: 0.75,
      busPrice: 1,
      isTwoWay: twoWayToggle,
    };

    return course;
  };

  // The courses array holds just start and end properties, this function
  //  allows searching by end used as start
  const reverseCourseStartEnd = (course: Route): Route => {
    if (course) {
      const temp = course.end;
      course.end = course.start;
      course.start = temp;
    }
    return course;
  };

  return (
    <>
      <div className="pt-[5.5rem] lg:pt-24 container">
        {/* <!-- Page Title --> */}
        <div className="flex flex-wrap">
          <div className="my-10 w-full  lg:w-1/2 md:px-12">
            <h1 className="pt-2 text-lg text-center">
              {t("title")}
              <strong className="font-bold">{t("title-strong")}</strong>
            </h1>
            <h2 className="pt-3 pb-6 sm:pb-4 text-sm">
              <span className="text-sm">{t("all-fixed-routes")}</span>
              <Link href="/routes">
                <a className="text-accent text-sm font-semibold cursor-pointer hover:underline">
                  {t("all-fixed-routes-button")}
                </a>
              </Link>
            </h2>
            <div className="flex items-center mb-2 w-full gap-1">
              <div className="w-1/2">
                <input
                  id="radio1"
                  className="radio-input"
                  name="radio-mode"
                  type="radio"
                />
                <label
                  htmlFor="radio1"
                  className="radio-label relative"
                  onClick={() => {
                    setMode("auto");
                    setManualMode(false);
                  }}
                >
                  <span className="text-sm">{t("auto-mode")}</span>
                  <span className="absolute text-sm -top-[14%] right-[4%] text-jacarta-100 bg-accent rounded-lg px-2">
                    {t("best-price")}
                  </span>
                </label>
              </div>
              <div className="w-1/2">
                <input
                  id="radio2"
                  className="radio-input"
                  name="radio-mode"
                  type="radio"
                  defaultChecked={true}
                />
                <label
                  htmlFor="radio2"
                  className="radio-label"
                  onClick={() => {
                    setManualMode(true);
                    setMode("manual");
                  }}
                >
                  {t("manual-mode")}
                </label>
              </div>
            </div>
            <div className="flex items-center mb-2 w-full gap-1">
              <div
                className="w-1/2"
                onClick={() => {
                  setTwoWayToggle(false);
                  setReturnDate(null);
                  dispatch(updateReturnDate(null));
                }}
              >
                <input
                  id="radio3"
                  className="radio-input"
                  name="radio"
                  type="radio"
                  defaultChecked={true}
                />
                <label htmlFor="radio3" className="radio-label text-sm">
                  <svg
                    width={18}
                    height={18}
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 24 24"
                    fill="currentColor"
                    className="mr-1"
                  >
                    <path
                      fillRule="evenodd"
                      d="M8.25 3.75H19.5a.75.75 0 01.75.75v11.25a.75.75 0 01-1.5 0V6.31L5.03 20.03a.75.75 0 01-1.06-1.06L17.69 5.25H8.25a.75.75 0 010-1.5z"
                      clipRule="evenodd"
                    />
                  </svg>
                  {t("one-way")}
                </label>
              </div>
              <div
                className="w-1/2"
                onClick={() => {
                  setTwoWayToggle(true);
                }}
              >
                <input
                  id="radio4"
                  className="radio-input"
                  name="radio"
                  type="radio"
                />
                <label htmlFor="radio4" className="text-sm radio-label">
                  <svg
                    width={20}
                    height={20}
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 24 24"
                    fill="currentColor"
                    className="mr-1"
                  >
                    <path
                      fillRule="evenodd"
                      d="M12 5.25c1.213 0 2.415.046 3.605.135a3.256 3.256 0 013.01 3.01c.044.583.077 1.17.1 1.759L17.03 8.47a.75.75 0 10-1.06 1.06l3 3a.75.75 0 001.06 0l3-3a.75.75 0 00-1.06-1.06l-1.752 1.751c-.023-.65-.06-1.296-.108-1.939a4.756 4.756 0 00-4.392-4.392 49.422 49.422 0 00-7.436 0A4.756 4.756 0 003.89 8.282c-.017.224-.033.447-.046.672a.75.75 0 101.497.092c.013-.217.028-.434.044-.651a3.256 3.256 0 013.01-3.01c1.19-.09 2.392-.135 3.605-.135zm-6.97 6.22a.75.75 0 00-1.06 0l-3 3a.75.75 0 101.06 1.06l1.752-1.751c.023.65.06 1.296.108 1.939a4.756 4.756 0 004.392 4.392 49.413 49.413 0 007.436 0 4.756 4.756 0 004.392-4.392c.017-.223.032-.447.046-.672a.75.75 0 00-1.497-.092c-.013.217-.028.434-.044.651a3.256 3.256 0 01-3.01 3.01 47.953 47.953 0 01-7.21 0 3.256 3.256 0 01-3.01-3.01 47.759 47.759 0 01-.1-1.759L6.97 15.53a.75.75 0 001.06-1.06l-3-3z"
                      clipRule="evenodd"
                    />
                  </svg>
                  {t("two-way")}
                </label>
              </div>
            </div>
            <div className="min-h-[96px]">
              {!manualMode && (
                <AutoCompleteFixed
                  courses={courses}
                  setStart={setStart}
                  setEnd={setEnd}
                  router={router}
                ></AutoCompleteFixed>
              )}
              {manualMode && (
                <AutocompletePoints
                  setStart={setStart}
                  setEnd={setEnd}
                  router={router}
                ></AutocompletePoints>
              )}
            </div>
            <div className="flex items-center mb-2 w-full">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width={22}
                height={22}
                viewBox="0 0 24 24"
                fill="currentColor"
                className="absolute ml-[7px] z-20 !text-accent"
              >
                <path
                  fillRule="evenodd"
                  d="M6.75 2.25A.75.75 0 017.5 3v1.5h9V3A.75.75 0 0118 3v1.5h.75a3 3 0 013 3v11.25a3 3 0 01-3 3H5.25a3 3 0 01-3-3V7.5a3 3 0 013-3H6V3a.75.75 0 01.75-.75zm13.5 9a1.5 1.5 0 00-1.5-1.5H5.25a1.5 1.5 0 00-1.5 1.5v7.5a1.5 1.5 0 001.5 1.5h13.5a1.5 1.5 0 001.5-1.5v-7.5z"
                  clipRule="evenodd"
                />
              </svg>
              <CustomDatePicker
                setDirection={setArrivalDate}
                destinationKey={"arrival"}
                minDate={null}
              />
            </div>
            {twoWayToggle && (
              <div className="flex items-center mb-2 w-full">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width={22}
                  height={22}
                  viewBox="0 0 24 24"
                  fill="currentColor"
                  className="absolute ml-[5px] z-20 !text-accent"
                >
                  <path
                    fillRule="evenodd"
                    d="M6.75 2.25A.75.75 0 017.5 3v1.5h9V3A.75.75 0 0118 3v1.5h.75a3 3 0 013 3v11.25a3 3 0 01-3 3H5.25a3 3 0 01-3-3V7.5a3 3 0 013-3H6V3a.75.75 0 01.75-.75zm13.5 9a1.5 1.5 0 00-1.5-1.5H5.25a1.5 1.5 0 00-1.5 1.5v7.5a1.5 1.5 0 001.5 1.5h13.5a1.5 1.5 0 001.5-1.5v-7.5z"
                    clipRule="evenodd"
                  />
                </svg>
                <CustomDatePicker
                  setDirection={setReturnDate}
                  destinationKey={"return"}
                  minDate={arrivalDate}
                />
              </div>
            )}
            <div className="flex flex-row gap-1 w-full">
              <div className="flex items-center w-1/2">
                <input
                  id="message"
                  className="pl-[10px] bg-jacarta-100  dark:bg-jacarta-700 hover:ring-accent/10 focus:ring-accent dark:border-jacarta-600 dark:placeholder:text-jacarta-300 w-full rounded-lg py-3 hover:ring-2 dark:text-white"
                  required
                  name="message"
                  type="number"
                  min={1}
                  value={adultsInput}
                  onChange={(event) => {
                    const value = event.target.value;
                    setAdultsInput(value);
                    dispatch(updateAdults(value));
                  }}
                  placeholder={t("adults")}
                ></input>
              </div>
              <div className="flex items-center w-1/2">
                <input
                  id="message"
                  className="pl-[10px] bg-jacarta-100 contact-form-input dark:bg-jacarta-700 hover:ring-accent/10 focus:ring-accent dark:border-jacarta-600 dark:placeholder:text-jacarta-300 w-full rounded-lg py-3 hover:ring-2 dark:text-white"
                  required
                  name="message"
                  type="number"
                  min={1}
                  value={childrenInput}
                  onChange={(event) => {
                    const value = event.target.value;
                    setChildrenInput(value);
                    dispatch(updateChildren(value));
                  }}
                  placeholder={t("children")}
                ></input>
              </div>
            </div>
            {validationError && (
              <div className="text-center text-red font-bold px-4 pt-4">
                {t("required-error")}
              </div>
            )}
            {notFound && (
              <div>
                <p className="text-center text-red font-bold px-4 pt-4">
                  {t("not-found")}
                </p>
              </div>
            )}
            <div className="py-5 flex justify-center">
              <button
                type="submit"
                className="flex items-center bg-accent shadow-accent-volume hover:bg-accent-dark rounded-full py-3 px-8 text-center font-semibold text-white transition-all"
                id="contact-form-submit"
                onClick={() => searchForCourse()}
                disabled={loadingToggle}
              >
                {t("check-available")}
                {loadingToggle && (
                  <svg
                    width={25}
                    height={25}
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                    strokeWidth="1.5"
                    stroke="currentColor"
                    className="animate-spin ml-1"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M16.023 9.348h4.992v-.001M2.985 19.644v-4.992m0 0h4.992m-4.993 0l3.181 3.183a8.25 8.25 0 0013.803-3.7M4.031 9.865a8.25 8.25 0 0113.803-3.7l3.181 3.182m0-4.991v4.99"
                    />
                  </svg>
                )}
              </button>
            </div>
          </div>
          <div className="flex flex-col gap-2 mb-10 md:mt-32 w-full lg:w-1/2 md:px-12">
            <div className="flex items-center py-2">
              <hr className="flex-grow border-t border-accent" />
              <span className="px-3 text-accent font-bold">
                {t("prices-discouns-title")}
              </span>
              <hr className="flex-grow border-t border-accent" />
            </div>
            <h2 className="pt-4 pb-2 px-2 text-sm text-center">
              {t("terms-details")}
            </h2>
            <div className="flex items-center py-2">
              <hr className="flex-grow border-t border-accent" />
              <span className="px-3 text-accent font-bold">
                {t("rules-services-title")}
              </span>
              <hr className="flex-grow border-t border-accent" />
            </div>
            <h3 className="pt-2 pb-6 px-2 text-sm text-center">
              {t("terms-details-2")}
            </h3>
          </div>
        </div>
      </div>
    </>
  );
};

export default Searchbox;
